//shared containers
import { Footer } from '@/containers-m/Footer';
import { Navigation } from '/containers-m/';

//containers
import {
  Hero,
  WatchItRunYourFreight,
  HowItWorks,
  RunsOnYourStack,
  TheRestOfTheLineup,
  EndCTA,
} from '../containers';

//styles
import {
  PageWrapper,
  DockScheduleManagementGlobalStyle,
} from './DockScheduleManagement.styles';

const hero = {
  slug: 'dock-schedule-management',
  title: 'Dock Schedule Management',
  description:
    'Keeps the Opendock dock schedule on plan: a briefing before the first shift, late arrivals moved to the next valid slot, and a reschedule plan ready when dock capacity drops. It acts where your configured Opendock rules stop.',
};

const watchItRun = {
  title: 'Dock capacity just dropped',
  stageHeadTitle:
    'Inbound Dock B lost a door to repair, with six appointments due before 3pm.',
  stageHeadMeta: 'Opendock · DC-02',
  rows: [
    {
      time: '12:40:00',
      text: 'Scheduler reported Inbound Dock B at half capacity until 3pm. 6 appointments lost capacity.',
    },
    {
      time: '12:40:06',
      text: 'Found the earliest valid slot on docks that accept the same load type for 5 of them.',
    },
    {
      time: '12:40:20',
      text: 'Reschedule plan sent to the scheduler, and approved.',
    },
    {
      time: '12:41:05',
      text: '5 appointments rescheduled in Opendock. Each carrier notified of its new time and dock.',
    },
    {
      time: '12:41:30',
      text: 'No valid slot left today for the 6th. Sent to the scheduler with the 3 closest open slots tomorrow.',
    },
  ],
  summaryText:
    'Dock capacity dropped in the middle of the day, and the scheduler told the agent which appointments were affected. Loadsmart AI found the earliest valid slot for five of the six, sent one reschedule plan for approval, told each carrier its new time and dock, and gave the scheduler the three closest open slots for the one it couldn’t place.',
  agentBadge: 'Dock Agent',
  escalationTitle: 'You decide how much it does on its own.',
  escalationText:
    'Each automation runs at the level you set: notify only, propose and wait for approval, or act. Briefings can go out on their own, while no-shows and reschedules wait for a scheduler’s approval until you’re ready to let them run.',
};

const howItWorks = {
  stepsTitleWhite: 'Runs the dock schedule all day,',
  stepsTitleGradient: 'from the morning briefing to the last door.',
  steps: [
    {
      title: 'Briefs the day',
      description:
        'Sends a dock plan before the first shift, with overbooked hours and appointments missing details flagged.',
    },
    {
      title: 'Chases missing details',
      description:
        'Opendock already sends confirmations and reminders from your configured rules. The agent goes further, chasing a carrier for a missing reference number before the truck reaches the gate.',
    },
    {
      title: 'Handles late arrivals and no-shows',
      description:
        'Moves a late truck to the next valid slot on the same dock. Once your late threshold passes, it proposes marking a no-show for approval, freeing the capacity and offering the carrier new slots.',
    },
    {
      title: 'Reschedules when dock capacity drops',
      description:
        'Finds the earliest valid slot on docks that accept the same load type, sends one plan for approval, then notifies the carriers. Appointments it can’t place go to the scheduler with the closest open slots.',
    },
  ],
  outcomes: [
    {
      title: 'Dock capacity stays in use',
      description:
        'Capacity freed by a no-show or a rescheduled appointment goes back on the schedule while there is still time to fill it.',
    },
    {
      title: 'The shift starts ahead',
      description:
        'Schedulers know the day’s problem hours before the first truck arrives.',
    },
    {
      title: 'Carriers arrive ready',
      description:
        'Reminders collect missing reference numbers before the truck reaches the gate.',
    },
  ],
};

const runsOnYourStackNote =
  'Appointment, arrival and status data come from Opendock. A capacity drop is reported by the scheduler, or by your maintenance system if you have one.';

const lineupCards = [
  {
    slug: 'scheduling-and-re-scheduling',
    title: 'Scheduling & Re-Scheduling',
    description: 'Books and rebooks the appointments this agent keeps on plan.',
  },
  {
    slug: 'detention-handling',
    title: 'Detention Handling',
    description: 'Watches each truck’s free time once it’s on site.',
  },
  {
    slug: 'document-collection-and-filing',
    title: 'Document Collection & Filing',
    description:
      'We capture every quote, tender, rate con, BOL, POD and invoice as it arrives, and file it against the right shipment automatically.',
  },
  {
    slug: 'tracking-and-status-updates',
    title: 'Tracking & Status Updates',
    description:
      'We get status from carriers so your team stops making check calls, and push clean updates to the people who need them.',
  },
  {
    slug: 'tender-failures',
    title: 'Tender Failures',
    description:
      'When a tender falls through, we re-tender down your routing guide and re-book, before the miss ever reaches your team.',
  },
  {
    slug: 'proactive-load-audits',
    title: 'Proactive Load Audits',
    description:
      'We check every load before pickup and flag at-risk transit early, so problems surface before they turn into penalties.',
  },
  {
    slug: 'claims-processing',
    title: 'Claims Processing',
    description:
      'We gather the documentation, validate the claim, and draft the response for your approval, so claims get filed and recovered.',
  },
  {
    slug: 'purchase-order-follow-up',
    title: 'Purchase Order Follow-Up',
    description:
      'We find purchase orders due with no appointment, and get the supplier or carrier to book before stock runs short.',
  },
  {
    slug: 'warehouse-customer-service',
    title: 'Warehouse Customer Service',
    description:
      'We answer your warehouse’s status questions and book appointments from an email, using your dock schedule and WMS.',
  },
  {
    slug: 'yard-task-orchestration',
    title: 'Yard Task Orchestration',
    description:
      'We select the asset for each pickup, schedule its preload and inspection, and return it to the yard, so it is ready when the carrier arrives.',
  },
];

function DockScheduleManagement() {
  return (
    <PageWrapper>
      <DockScheduleManagementGlobalStyle />
      <Navigation theme="dark" />
      <Hero {...hero} />
      <WatchItRunYourFreight {...watchItRun} />
      <HowItWorks {...howItWorks} />
      <RunsOnYourStack note={runsOnYourStackNote} />
      <TheRestOfTheLineup cards={lineupCards} />
      <EndCTA agentName="Dock Schedule Management" />
      <Footer />
    </PageWrapper>
  );
}

export default DockScheduleManagement;
export { DockScheduleManagement };
